/**
 * WebSocket service for DQ Rules with real-time AI thinking process
 * Integrates with text-to-sql-streaming API for AI-powered rule creation
 */

import { WS_BASE_URL } from '../config/env';
import type { SuggestedRule } from '../types/dqRulesLlm';

// Helper function to map WebSocket completion result to SuggestedRule
export function mapWebSocketResultToRule(result: WebSocketCompletionResult, targetColumn?: string): SuggestedRule | null {
    if (!result.final_result || !result.final_result.success) {
        return null;
    }

    // Handle new unified structure first
    if (result.final_result.solution) {
        const solution = result.final_result.solution;

        // Handle static_dq_rules response type
        if (result.final_result.response_type === 'static_dq_rules' && solution.type === 'dq_rule') {
            return {
                rule_type: solution.rule_type as any,
                parameters: solution.parameters || {},
                rule_description: result.final_result.rule_description,
                column_name: solution.column_name || targetColumn
            };
        }

        // Handle sql_generated response type - try to derive rule from SQL
        if (result.final_result.response_type === 'sql_generated' && solution.type === 'sql_query' && solution.content) {
            const originalSql = solution.content; // Keep original SQL
            const sql = originalSql.toLowerCase();
            const understanding = result.final_result.llm_understanding || '';

            // For SQL-generated rules, use ai_generated_rule type with SQL in parameters
            const parameters: Record<string, any> = {
                custom_sql: originalSql,
                validation_description: understanding,
                generated_by_llm: true,
                llm_understanding: understanding
            };

            // Try to extract additional parameters based on SQL patterns
            if (sql.includes('between') || understanding.toLowerCase().includes('between')) {
                const rangeMatch = understanding.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/i);
                if (rangeMatch) {
                    parameters.min_value = parseFloat(rangeMatch[1]);
                    parameters.max_value = parseFloat(rangeMatch[2]);
                }
            } else if (sql.includes('length(') || understanding.toLowerCase().includes('length')) {
                const lengthMatch = understanding.match(/length.*between\s+(\d+)\s+and\s+(\d+)/i);
                if (lengthMatch) {
                    parameters.min_length = parseInt(lengthMatch[1]);
                    parameters.max_length = parseInt(lengthMatch[2]);
                }
            }

            return {
                rule_type: 'ai_generated_rule' as any,
                parameters,
                rule_description: result.final_result.rule_description || understanding || 'AI-generated SQL rule',
                column_name: targetColumn
            };
        }
    }

    // Legacy handling for old API responses
    // If the WebSocket provides DQ rules recommendations, use the first one
    if (result.final_result.response_type === 'dq_rules_recommended' && result.final_result.suggested_rules?.length > 0) {
        const firstRule = result.final_result.suggested_rules[0];
        return {
            rule_type: firstRule.rule_type as any,
            parameters: firstRule.parameters,
            rule_description: firstRule.rule_description,
            column_name: firstRule.column_name || targetColumn // Use API column_name or fallback
        };
    }

    // If we have an SQL query, try to derive a rule from it (legacy)
    if (result.final_result.response_type === 'sql_generated' && (result.final_result.sql_query || result.final_result.sql_content)) {
        const originalSql = result.final_result.sql_query || result.final_result.sql_content || '';
        const sql = originalSql.toLowerCase();
        const understanding = result.final_result.llm_understanding || '';

        // For SQL-generated rules, use ai_generated_rule type with SQL in parameters
        const parameters: Record<string, any> = {
            custom_sql: originalSql,
            validation_description: understanding,
            generated_by_llm: true,
            llm_understanding: understanding
        };

        // Try to extract additional parameters based on SQL patterns for backward compatibility
        if (sql.includes('between') || understanding.toLowerCase().includes('between')) {
            const rangeMatch = understanding.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/i);
            if (rangeMatch) {
                parameters.min_value = parseFloat(rangeMatch[1]);
                parameters.max_value = parseFloat(rangeMatch[2]);
            }
        } else if (sql.includes('length(') || understanding.toLowerCase().includes('length')) {
            const lengthMatch = understanding.match(/length.*between\s+(\d+)\s+and\s+(\d+)/i);
            if (lengthMatch) {
                parameters.min_length = parseInt(lengthMatch[1]);
                parameters.max_length = parseInt(lengthMatch[2]);
            }
        } else if (sql.includes(' in (') || understanding.toLowerCase().includes('must be one of')) {
            // Try to extract list of values
            const listMatch = understanding.match(/one of[:\s]*([^.]+)/i);
            if (listMatch) {
                const values = listMatch[1].split(',').map(v => v.trim().replace(/['"]/g, ''));
                parameters.values = values;
            }
        }

        return {
            rule_type: 'ai_generated_rule' as any,
            parameters,
            rule_description: understanding || result.final_result.explanation || 'Generated rule from AI analysis',
            column_name: targetColumn
        };
    }

    // Comprehensive fallback: try to extract SQL from any available content
    const understanding = result.final_result.llm_understanding || '';
    const ruleDescription = result.final_result.rule_description || '';
    const explanation = result.final_result.explanation || '';

    // Try to extract SQL from various text fields
    const possibleSqlSources = [
        result.final_result.solution?.content,
        result.final_result.sql_query,
        result.final_result.sql_content,
        understanding,
        ruleDescription,
        explanation
    ].filter(Boolean);

    for (const source of possibleSqlSources) {
        const extractedSql = extractSqlFromText(source);
        if (extractedSql && extractedSql.toLowerCase().includes('select')) {
            return {
                rule_type: 'ai_generated_rule' as any,
                parameters: {
                    custom_sql: extractedSql,
                    validation_description: understanding || ruleDescription || explanation,
                    generated_by_llm: true,
                    original_prompt: ruleDescription || understanding
                },
                rule_description: ruleDescription || understanding || 'AI-generated SQL rule',
                column_name: targetColumn
            };
        }
    }

    // If we still don't have SQL but have understanding, create rule without SQL
    if (understanding || ruleDescription) {
        return {
            rule_type: 'ai_generated_rule' as any,
            parameters: {
                validation_description: understanding || ruleDescription,
                generated_by_llm: true,
                original_prompt: ruleDescription || understanding
            },
            rule_description: ruleDescription || understanding,
            column_name: targetColumn
        };
    }

    return null;
}

// Helper function to extract SQL from text
function extractSqlFromText(text: string): string | null {
    if (!text) return null;

    // Look for SQL patterns
    const sqlPatterns = [
        /```sql\s*([\s\S]*?)\s*```/gi,
        /```\s*(SELECT[\s\S]*?)\s*```/gi,
        /\b(SELECT\s+[\s\S]*?(?:;|\n\s*\n|$))/gi
    ];

    for (const pattern of sqlPatterns) {
        const matches = text.match(pattern);
        if (matches && matches[0]) {
            return matches[0]
                .replace(/```sql/gi, '')
                .replace(/```/g, '')
                .trim();
        }
    }

    // If text contains SELECT and FROM, it might be SQL itself
    if (text.toLowerCase().includes('select') && text.toLowerCase().includes('from')) {
        return text.trim();
    }

    return null;
}

export interface ColumnMetadata {
    data_type: 'string' | 'double' | 'bigint' | 'boolean' | 'date';
    sample_values: string[];
    description: string;
    has_quality_issues: boolean;
    dq_rules: any; // null or rule object
}

export interface WebSocketTextToSQLRequest {
    natural_language_query: string; // Primary field per API spec
    table_name: string; // Required field
    table_metadata: {
        file_name: string;
        description?: string;
        column_description: Record<string, ColumnMetadata>; // API expects dictionary of ColumnMetadata
    };
    user_email: string; // Required: authenticated user's email
    user_query?: string; // Keep for compatibility
    target_language?: string; // Language code for LLM response translation (e.g. 'hi', 'fr'). Omit or null for English.
}

export interface WebSocketProgressUpdate {
    type: "progress";
    stage: string;
    message: string;
    user_friendly_thinking?: string;
    technical_detail?: string;
    confidence_level?: number;
    reasoning_category?: string;
    timestamp: string;
}

// Actual WebSocket API response format (includes step, message, and final_result)
export interface WebSocketAPIResponse {
    step?: string; // Progress step: analyzing, generating, completed, etc.
    message?: string;
    sql_content?: string | null; // API may include SQL content
    error_feedback?: string | null;
    iteration?: number;
    reasoning_step?: string; // API includes reasoning step like "Final Result"
    intermediate_thinking?: string; // AI thinking process details
    final_result?: any; // API includes final_result with complex structure
    error?: string;
}

export interface WebSocketCompletionResult {
    type: "completion";
    final_result: {
        response_type: "sql_generated" | "static_dq_rules" | "dq_rules_recommended";
        success: boolean;
        solution: {
            type: "sql_query" | "dq_rule";
            content?: string; // For SQL queries
            rule_type?: string; // For DQ rules
            parameters?: Record<string, any>; // For DQ rules
            column_name?: string; // For DQ rules
        };
        llm_understanding: string;
        rule_description: string;
        reasoning: string;
        confidence: number;
        validation_results: {
            dq_validation: {
                performed: boolean;
                results: string;
            };
            execution_results: {
                sample_rows: Array<Record<string, any>>;
                column_names?: string[];
            };
        };
        // Legacy optional fields used by client code
        classification_results?: { classification: string };
        execution_results?: any;

        // Legacy fields that might still exist
        explanation?: string;
        confidence_score?: number;
        suggested_rules?: Array<{
            rule_type: string;
            rule_description: string;
            parameters: Record<string, any>;
            column_name?: string;
        }>;
        sql_query?: string;
        sql_content?: string;

        // DQ validation results from actual API (legacy)
        dq_sample_validation?: {
            performed: boolean;
            summary: {
                status: string;
                total_rows_validated: number;
                total_columns_validated: number;
                columns_with_failures: number;
                overall_pass_rate: number;
                total_failed_records: number;
                total_samples: number;
                passed_samples: number;
                failed_samples: number;
                pass_rate: number;
                pass_rate_percentage: string;
                sample_summary: {
                    total_rows_validated: number;
                    columns_validated: string[];
                    total_rules_executed: number;
                    validation_timestamp: string;
                };
                validation_details: Array<{
                    column_name: string;
                    rule_type: string;
                    rule_description?: string;
                    sample_values?: string[];
                    passed_values?: string[];
                    failed_values?: string[];
                    validation_results?: Array<{
                        value: any;
                        passed: boolean;
                        reason: string;
                    }>;
                }>;
            };
            results: {
                success: boolean;
                column_results: Record<string, {
                    pass_rate: number;
                    fail_count: number;
                    rules: Array<{
                        rule_type: string;
                        pass_rate: number;
                        fail_count: number;
                    }>;
                }>;
                overall_summary: {
                    total_records: number;
                    avg_pass_rate: number;
                };
            };
        };

        // Other fields from actual API
        reasoning_steps?: string[];
        clarifying_questions?: string[]; // LLM clarifying questions (translated when target_language set)
        classification_reasoning?: string; // LLM classification reasoning (translated when target_language set)
        query_classification?: {
            classification: string;
            requires_sql: boolean;
        };
    };
}

export interface WebSocketErrorResult {
    type: "error";
    message: string;
    timestamp: string;
}

export type WebSocketStreamUpdate = WebSocketProgressUpdate | WebSocketCompletionResult | WebSocketErrorResult | WebSocketAPIResponse;
// Narrow internal stream update to only typed internal messages (progress/completion/error)
export type WebSocketInternalUpdate = WebSocketProgressUpdate | WebSocketCompletionResult | WebSocketErrorResult;

export interface DqRulesWebSocketService {
    connect(clientId: string): Promise<void>;
    sendQuery(request: WebSocketTextToSQLRequest): void;
    disconnect(): void;
    isConnected(): boolean;
    onProgress(callback: (update: WebSocketProgressUpdate) => void): void;
    onCompletion(callback: (result: WebSocketCompletionResult) => void): void;
    onError(callback: (error: WebSocketErrorResult) => void): void;
    clearCallbacks(): void;
}

class DqRulesWebSocketClient implements DqRulesWebSocketService {
    private websocket: WebSocket | null = null;
    private baseUrl: string = WS_BASE_URL || 'ws://127.0.0.1:8000';
    private progressCallbacks: Array<(update: WebSocketProgressUpdate) => void> = [];
    private completionCallbacks: Array<(result: WebSocketCompletionResult) => void> = [];
    private errorCallbacks: Array<(error: WebSocketErrorResult) => void> = [];
    private reconnectAttempts: number = 0;
    private maxReconnectAttempts: number = 3;

    async connect(clientId: string): Promise<void> {
        // Prevent duplicate connections
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            return Promise.resolve();
        }

        // Close any existing connection first
        if (this.websocket) {
            this.websocket.close();
        }

        return new Promise((resolve, reject) => {
            const wsUrl = `${this.baseUrl}/api/v1/text-to-sql-streaming/ws/${clientId}`;
            this.websocket = new WebSocket(wsUrl);

            const timeout = setTimeout(() => {
                reject(new Error('WebSocket connection timeout'));
            }, 10000);

            this.websocket.onopen = () => {
                clearTimeout(timeout);
                this.reconnectAttempts = 0;
                resolve();
            };

            this.websocket.onmessage = (event) => {
                try {
                    const apiResponse: WebSocketAPIResponse = JSON.parse(event.data);

                    // Convert API response format to internal format
                    const update = this.convertAPIResponseToInternalFormat(apiResponse);

                    if (update) {
                        this.handleMessage(update as WebSocketInternalUpdate);
                    }
                } catch (error) {
                    console.error('Failed to parse WebSocket message:', error);
                    this.notifyError({
                        type: 'error',
                        message: 'Failed to parse server response',
                        timestamp: new Date().toISOString()
                    });
                }
            };

            this.websocket.onclose = (event) => {
                clearTimeout(timeout);

                if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
                    setTimeout(() => {
                        this.reconnectAttempts++;
                        this.connect(clientId).catch(console.error);
                    }, Math.pow(2, this.reconnectAttempts) * 1000);
                }
            };

            this.websocket.onerror = (error) => {
                clearTimeout(timeout);
                console.error('WebSocket error:', error);
                reject(new Error('WebSocket connection failed'));
            };
        });
    }

    sendQuery(request: WebSocketTextToSQLRequest): void {
        if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket is not connected');
        }

        this.websocket.send(JSON.stringify(request));
    }

    disconnect(): void {
        if (this.websocket) {
            this.websocket.close(1000, 'User requested disconnect');
            this.websocket = null;
        }

        // Clear all callback arrays to prevent duplicate listeners
        this.progressCallbacks = [];
        this.completionCallbacks = [];
        this.errorCallbacks = [];
    }

    isConnected(): boolean {
        return this.websocket?.readyState === WebSocket.OPEN;
    }

    onProgress(callback: (update: WebSocketProgressUpdate) => void): void {
        this.progressCallbacks.push(callback);
    }

    onCompletion(callback: (result: WebSocketCompletionResult) => void): void {
        this.completionCallbacks.push(callback);
    }

    onError(callback: (error: WebSocketErrorResult) => void): void {
        this.errorCallbacks.push(callback);
    }

    // Clear all callback arrays - useful before setting up new listeners
    clearCallbacks(): void {
        this.progressCallbacks = [];
        this.completionCallbacks = [];
        this.errorCallbacks = [];
    }

    private convertAPIResponseToInternalFormat(apiResponse: WebSocketAPIResponse): WebSocketStreamUpdate | null {
        const timestamp = new Date().toISOString();

        // Handle completion step specifically - this is the key completion trigger
        if (apiResponse.step === 'completed' && apiResponse.final_result) {
            return {
                type: 'completion',
                final_result: apiResponse.final_result
            };
        }

        // Handle final result from other sources (fallback)
        if (apiResponse.final_result) {
            return {
                type: 'completion',
                final_result: apiResponse.final_result
            };
        }

        // Handle error responses
        if (apiResponse.error || apiResponse.error_feedback) {
            return {
                type: 'error',
                message: apiResponse.error || apiResponse.error_feedback || 'Unknown error occurred',
                timestamp
            };
        }

        // Handle progress updates based on step (but filter out connection and completion messages)
        if (apiResponse.step && apiResponse.message) {
            // Skip connection and processing_complete messages - don't show to user
            if (apiResponse.step === 'connected' || apiResponse.step === 'processing_complete') {
                return null;
            }

            return {
                type: 'progress',
                stage: apiResponse.step,
                message: apiResponse.message,
                user_friendly_thinking: apiResponse.intermediate_thinking,
                technical_detail: apiResponse.intermediate_thinking,
                confidence_level: undefined, // Not provided in most steps
                reasoning_category: apiResponse.reasoning_step || apiResponse.step,
                timestamp
            };
        }

        return null;
    }

    private handleMessage(update: WebSocketInternalUpdate): void {
        switch (update.type) {
            case 'progress':
                this.notifyProgress(update);
                break;
            case 'completion':
                this.notifyCompletion(update);
                break;
            case 'error':
                this.notifyError(update);
                break;
        }
    }

    private notifyProgress(update: WebSocketProgressUpdate): void {
        this.progressCallbacks.forEach(callback => {
            try {
                callback(update);
            } catch (error) {
                console.error('Progress callback error:', error);
            }
        });
    }

    private notifyCompletion(result: WebSocketCompletionResult): void {
        this.completionCallbacks.forEach((callback, index) => {
            try {
                callback(result);
            } catch (error) {
                console.error(`Completion callback ${index} error:`, error);
            }
        });
    }

    private notifyError(error: WebSocketErrorResult): void {
        this.errorCallbacks.forEach(callback => {
            try {
                callback(error);
            } catch (error) {
                console.error('Error callback error:', error);
            }
        });
    }
}

// Export singleton instance
export const dqRulesWebSocketService = new DqRulesWebSocketClient();

// Generate unique client ID
export function generateClientId(): string {
    return `dq-rules-client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}